// src/components/ui/popups/GlobalPopupContainer.jsx
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getPopupConfig } from '../../../service-scripts/popupApi';
import GlobalPopup from './GlobalPopup';

const MAX_CLOSES = 4;

const GlobalPopupContainer = () => {
  const [popupConfig, setPopupConfig] = useState(null);
  const [popupKey, setPopupKey] = useState(null); // localStorage key
  const [loaded, setLoaded] = useState(false);
  const [showBar, setShowBar] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false); // ✅ new state

  const location = useLocation();

  // 1) Load config once and compute the key
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const config = await getPopupConfig();
        if (!isMounted) return;

        if (!config || config.status !== 'active') {
          setPopupConfig(config || null);
          setLoaded(true);
          return;
        }

        const keyBase =
          config.localStorageKey ||
          `global_popup_${config._id || config.name || 'default'}`;

        const versionSuffix = config.updatedAt || config.version || '';
        const fullKey = versionSuffix ? `${keyBase}_${versionSuffix}` : keyBase;

        setPopupConfig(config);
        setPopupKey(fullKey);
        setLoaded(true);
      } catch (err) {
        console.error('[GlobalPopupContainer] Failed to load popup config:', err);
        if (isMounted) setLoaded(true);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  // ✅ 1.5) Check whether the user has already responded (whether _answered exists)
  useEffect(() => {
    if (!popupKey || typeof window === 'undefined') return;

    try {
      const answeredRaw = window.localStorage.getItem(`${popupKey}_answered`);
      if (answeredRaw === '1') {
        setHasAnswered(true);
      }
    } catch (e) {
      console.warn('[GlobalPopupContainer] localStorage answered read failed', e);
    }
  }, [popupKey]);

  // 2) On each page change (pathname), decide whether to show the bar
  useEffect(() => {
    if (!loaded || !popupConfig || popupConfig.status !== 'active' || !popupKey) {
      return;
    }

    let closeCount = 0;
    try {
      const raw =
        typeof window !== 'undefined'
          ? window.localStorage.getItem(popupKey)
          : null;
      if (raw != null) closeCount = Number(raw) || 0;
    } catch (e) {
      console.warn('[GlobalPopupContainer] localStorage read failed', e);
    }

    if (closeCount >= MAX_CLOSES) {
      // user closed the popup multiple times — do not show it again
      setShowBar(false);
      setShowPopup(false);
      return;
    }

    // reset the bar and start a new timer on this page
    setShowBar(false);
    const timer = setTimeout(() => {
      setShowBar(true);
    }, 4000);

    return () => clearTimeout(timer);
  }, [loaded, popupConfig, popupKey, location.pathname]);

  const handleOpenPopup = () => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);

    if (!popupKey) return;

    try {
      const raw =
        typeof window !== 'undefined'
          ? window.localStorage.getItem(popupKey)
          : null;
      const prev = raw != null ? Number(raw) || 0 : 0;
      const next = prev + 1;

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(popupKey, String(next));
      }

      if (next >= MAX_CLOSES) {
        // limit reached — do not show the bar anymore
        setShowBar(false);
      }
    } catch (e) {
      console.warn('[GlobalPopupContainer] localStorage write failed', e);
    }
  };

  if (!loaded || !popupConfig || popupConfig.status !== 'active') {
    return null;
  }

  return (
    <>
      {/* Mini bar in the bottom-right corner */}
      <div
        className={`
          fixed z-40
          bottom-3 right-3
          max-w-xs w-80
          transition-all duration-300 ease-out
          ${showBar ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}
        `}
      >
        <div
          className="
            relative
            rounded-2xl
            shadow-2xl
            bg-gradient-to-r from-zinc-900 via-slate-900 to-zinc-900
            text-white
            px-4 py-3
            overflow-hidden
            border border-yellow-400/40
          "
        >
          {/* Decorative highlight */}
          <div className="pointer-events-none absolute -right-6 -top-6 h-16 w-16 rounded-full bg-yellow-400/20 blur-xl" />
  
          {/* Close button in the top-right corner */}
          <button
            type="button"
            onClick={() => setShowBar(false)}
            className="absolute cursor-pointer right-2 top-2 text-white/60 hover:text-white text-xs"
            aria-label="Hide announcement"
          >
            ✕
          </button>
  
          <div className="flex flex-col gap-1 pr-5">
            {/* Badge */}
            <span className="inline-flex items-center rounded-full bg-yellow-400/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-yellow-300">
              {popupConfig.title || 'New on Crino'}
            </span>
  
            {/* Text below the badge — changes if the code already exists */}
            {popupConfig.discount_value && (
              <p className="text-[11px] text-white/80 mt-0.5">
                {hasAnswered ? (
                  <>
                    Don’t forget to use your{' '}
                    <span className="font-semibold text-yellow-300">discount code</span>{' '}
                    at checkout.
                  </>
                ) : (
                  <>
                    Want to get{' '}
                    <span className="font-semibold text-yellow-300">
                      {popupConfig.discount_value}
                    </span>{' '}
                    off?
                  </>
                )}
              </p>
            )}
  
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={handleOpenPopup}
                className="
                  cursor-pointer inline-flex items-center px-3 py-1.5 rounded-full
                  bg-yellow-400 text-zinc-900
                  text-xs font-semibold
                  hover:bg-yellow-300
                  transition
                "
              >
                {hasAnswered ? 'View my code' : 'Learn more'}
              </button>
            </div>
          </div>
        </div>
      </div>
  
      {/* Popup overlay */}
      {showPopup && (
        <GlobalPopup
          title={popupConfig.title}
          text={popupConfig.text}
          valid_until={popupConfig.valid_until}
          cta={popupConfig.cta}
          imageUrl={popupConfig.imageUrl}
          closeable={popupConfig.closeable}
          discountCode={popupConfig.discount_code}
          storageKey={popupKey}
          onClose={handleClosePopup}
          onAnswered={() => setHasAnswered(true)}  // ✅ notify that the user has responded
        />
      )}
    </>
  );  
};

export default GlobalPopupContainer;
